import { useQuery } from '@tanstack/react-query';
import AppAuth from 'AppAuth';
import AppStore from 'fetch/AppFetch';
import { USER_TYPE_ZYWAVE, FIVE_MINUTES } from 'lib/config/appConstants';

/**
 * React Query hook for Zywave feature licenses and agencies.
 *
 * Features:
 * - Refetches on window focus when data is stale (5+ minutes old)
 * - Refetches on component mount when data is stale
 * - Keeps localStorage in sync for legacy code compatibility
 *
 * @param userId - Optional user identifier.
 * @param accountType - Optional account type used to determine if Zywave features are enabled.
 *
 * @returns The query result, including data, loading state, and error state.
 *          Returns undefined for non-Zywave users.
 *
 * @remarks
 * - The query is enabled when `userId` is provided and `accountType` matches `USER_TYPE_ZYWAVE`.
 * - Profile switches cause a page reload, which triggers a fresh fetch.
 */
export function useZywaveFeatures(userId?: string, accountType?: number) {
  const isEnabled = !!userId && accountType === USER_TYPE_ZYWAVE;

  const query = useQuery({
    queryKey: ['zywaveFeatures', userId],
    queryFn: async () => {
      const features = await AppAuth.getZywaveFeatureLicenseAgencies();
      // Store in localStorage for legacy code compatibility
      AppStore.storeZywaveUserFeatureAgencies(features || {});
      return features;
    },
    enabled: isEnabled,
    staleTime: FIVE_MINUTES,
    gcTime: FIVE_MINUTES,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Clear localStorage for non-Zywave users
  if (accountType !== USER_TYPE_ZYWAVE) {
    AppStore.deleteZywaveUserFeatureAgencies();
    return undefined;
  }

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isSuccess: query.isSuccess,
  };
}
